// ============================================================
// RecoverModal.jsx — Flujo de recuperación de contraseña
// ============================================================
// ¿QUÉ HACE ESTE ARCHIVO?
//   Maneja 3 pasos para recuperar la contraseña olvidada:
//
//   PASO 1 — Ingresar email
//     El cliente escribe su correo y el sistema envía un PIN
//     de 6 dígitos a ese correo (válido 5 minutos).
//
//   PASO 2 — Ingresar el PIN
//     6 inputs individuales. Al escribir cada dígito salta
//     automáticamente al siguiente. El PIN se valida con el servidor
//     mediante clienteValidarPin.php. Si es correcto, se muestra un mensaje
//     de éxito y se avanza al paso 3. Si es incorrecto, se muestra error.
//
//   PASO 3 — Crear nueva contraseña
//     El cliente escribe y confirma su nueva contraseña.
//     Se envía al servidor (clienteNuevaContrasena.php) para guardarla.
//
// PROPS QUE RECIBE:
//   onVolver()        → regresa al formulario de login
//   onExito()         → se llama cuando la contraseña se cambió exitosamente
//
// ENDPOINTS PHP REQUERIDOS:
//   clienteRecuperarPin.php    → recibe: email → envía PIN
//   clienteValidarPin.php      → recibe: email, pin → valida el PIN
//   clienteNuevaContrasena.php → recibe: email, pin, nueva_contrasena
// ============================================================

import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import baseURL from '../url';
import './RecoverModal.css';

export default function RecoverModal({ onVolver, onExito }) {

    // ── Paso actual: 1 | 2 | 3 ──────────────────────────────
    const [paso, setPaso] = useState(1);

    // ── Campos de cada paso ──────────────────────────────────
    const [recEmail,     setRecEmail]     = useState('');
    const [recPin,       setRecPin]       = useState(['', '', '', '', '', '']); // 6 celdas
    const [nuevaPass,    setNuevaPass]    = useState('');
    const [confirmaPass, setConfirmaPass] = useState('');

    // ── Mostrar / ocultar contraseña ─────────────────────────
    const [verPass, setVerPass] = useState(false);

    // ── Refs para los 6 inputs del PIN (foco automático) ─────
    const pinRefs = useRef([]);

    // ── Estado de carga y mensajes ───────────────────────────
    const [error,   setError]   = useState('');
    const [exito,   setExito]   = useState('');
    const [loading, setLoading] = useState(false);

    // ── Completado ───────────────────────────────────────────
    const [completado, setCompletado] = useState(false);

    // ────────────────────────────────────────────────────────
    // HELPERS
    // ────────────────────────────────────────────────────────

    const limpiarMensajes = () => { setError(''); setExito(''); };

    const resetPin = () => setRecPin(['', '', '', '', '', '']);

    // ────────────────────────────────────────────────────────
    // PASO 1: enviar PIN al correo
    // ────────────────────────────────────────────────────────

    const handleEnviarPin = async () => {
        limpiarMensajes();

        if (!recEmail) {
            setError('Ingresa tu correo electrónico');
            return;
        }

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('email', recEmail);

            const res  = await fetch(`${baseURL}/clienteRecuperarPin.php`, { method: 'POST', body: fd });
            const data = await res.json();

            if (data.ok) {
                setExito(data.mensaje || '¡Código enviado! Revisa tu correo y también la carpeta de spam.');
                // En desarrollo: si el servidor devuelve el PIN para pruebas
                if (data.pin_debug) console.info('PIN (solo desarrollo):', data.pin_debug);
                setTimeout(() => {
                    limpiarMensajes();
                    setPaso(2);
                }, 1800);
            } else {
                setError(data.error || 'No se pudo enviar el código. Verifica el correo.');
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        }
        setLoading(false);
    };

    // ────────────────────────────────────────────────────────
    // PASO 2: validar PIN con el servidor (NUEVO)
    // ────────────────────────────────────────────────────────

    const handleValidarPin = async () => {
        limpiarMensajes();

        if (recPin.join('').length < 6) {
            setError('Ingresa el código completo de 6 dígitos');
            return;
        }

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('email', recEmail);
            fd.append('pin', recPin.join(''));

            const res = await fetch(`${baseURL}/clienteValidarPin.php`, {
                method: 'POST',
                body: fd
            });
            const data = await res.json();

            if (data.ok) {
                setExito(data.mensaje || 'Código correcto');
                setTimeout(() => {
                    limpiarMensajes();
                    setPaso(3);
                }, 1500);
            } else {
                setError(data.error || 'Código incorrecto');
                // Si el PIN expiró, reiniciar flujo
                if (data.error?.toLowerCase().includes('expirado')) {
                    setTimeout(() => {
                        setPaso(1);
                        resetPin();
                        limpiarMensajes();
                    }, 2000);
                }
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        }
        setLoading(false);
    };

    // ────────────────────────────────────────────────────────
    // PASO 3: guardar nueva contraseña
    // ────────────────────────────────────────────────────────

    const handleNuevaContrasena = async () => {
        limpiarMensajes();

        if (!nuevaPass || !confirmaPass) {
            setError('Completa ambos campos');
            return;
        }
        if (nuevaPass.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        if (nuevaPass !== confirmaPass) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('email',            recEmail);
            fd.append('pin',              recPin.join(''));
            fd.append('nueva_contrasena', nuevaPass);

            const res  = await fetch(`${baseURL}/clienteNuevaContrasena.php`, { method: 'POST', body: fd });
            const data = await res.json();

            if (data.ok) {
                setCompletado(true);
                setTimeout(() => onExito(), 2500);
            } else {
                setError(data.error || 'Error al cambiar la contraseña');
                if (data.error?.toLowerCase().includes('expirado') ||
                    data.error?.toLowerCase().includes('incorrecto')) {
                    setTimeout(() => {
                        setPaso(1);
                        resetPin();
                        limpiarMensajes();
                    }, 2200);
                }
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        }
        setLoading(false);
    };

    // ────────────────────────────────────────────────────────
    // MANEJO DE LOS 6 INPUTS DEL PIN
    // ────────────────────────────────────────────────────────

    const handlePinChange = (index, value) => {
        if (!/^\d*$/.test(value)) return; // solo dígitos

        const nuevo = [...recPin];
        nuevo[index] = value.slice(-1); // máximo 1 dígito
        setRecPin(nuevo);

        if (value && index < 5) pinRefs.current[index + 1]?.focus();
    };

    const handlePinKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !recPin[index] && index > 0) {
            pinRefs.current[index - 1]?.focus();
        }
    };

    // ────────────────────────────────────────────────────────
    // RENDER
    // ────────────────────────────────────────────────────────

    if (completado) {
        return (
            <div className="rm-exito">
                <FontAwesomeIcon icon={faCheckCircle} className="rm-exito-icon" />
                <h3>¡Contraseña actualizada!</h3>
                <p>Tu contraseña fue cambiada exitosamente.<br />Serás redirigido al inicio de sesión...</p>
            </div>
        );
    }

    return (
        <div className="rm-form">

            {/* ── Indicador de pasos 1 → 2 → 3 ───────────── */}
            <div className="rm-steps">
                {[1, 2, 3].map(n => (
                    <React.Fragment key={n}>
                        <div className={`rm-step ${paso >= n ? 'activo' : ''}`}>
                            <span>{n}</span>
                        </div>
                        {n < 3 && <div className={`rm-step-line ${paso > n ? 'activo' : ''}`} />}
                    </React.Fragment>
                ))}
            </div>

            {/* ════════════════════════════════════════════════
                PASO 1: Ingresar correo
                ════════════════════════════════════════════════ */}
            {paso === 1 && (
                <>
                    <p className="rm-desc">
                        Escribe el correo de tu cuenta y te enviaremos un código de 6 dígitos para restablecer tu contraseña.
                    </p>
                    <div className="rm-field">
                        <label htmlFor="rm-email">Correo electrónico</label>
                        <input
                            id="rm-email"
                            type="email"
                            placeholder="ejemplo@correo.com"
                            value={recEmail}
                            onChange={e => setRecEmail(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleEnviarPin()}
                            autoComplete="email"
                        />
                    </div>
                </>
            )}

            {/* ════════════════════════════════════════════════
                PASO 2: Ingresar PIN
                ════════════════════════════════════════════════ */}
            {paso === 2 && (
                <>
                    <p className="rm-desc">
                        Ingresa el código de 6 dígitos que enviamos a <strong>{recEmail}</strong>.
                        {/* Texto actualizado a 5 minutos */}
                        Válido por <strong>5 minutos</strong>. Revisa también tu carpeta de spam.
                    </p>
                    <div className="rm-pin-wrap">
                        {recPin.map((digito, i) => (
                            <input
                                key={i}
                                ref={el => (pinRefs.current[i] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                className="rm-pin-input"
                                value={digito}
                                onChange={e => handlePinChange(i, e.target.value)}
                                onKeyDown={e => handlePinKeyDown(i, e)}
                                onFocus={e => e.target.select()}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        className="rm-resend-btn"
                        onClick={() => {
                            resetPin();
                            limpiarMensajes();
                            handleEnviarPin();
                        }}
                        disabled={loading}
                    >
                        ¿No recibiste el código? Reenviar
                    </button>
                </>
            )}

            {/* ════════════════════════════════════════════════
                PASO 3: Nueva contraseña
                ════════════════════════════════════════════════ */}
            {paso === 3 && (
                <>
                    <p className="rm-desc">Crea una nueva contraseña segura para tu cuenta.</p>

                    <div className="rm-field">
                        <label htmlFor="rm-nueva">Nueva contraseña</label>
                        <div className="rm-pass-wrap">
                            <input
                                id="rm-nueva"
                                type={verPass ? 'text' : 'password'}
                                placeholder="Mínimo 6 caracteres"
                                value={nuevaPass}
                                onChange={e => setNuevaPass(e.target.value)}
                                autoComplete="new-password"
                            />
                            <button type="button" className="rm-eye-btn" onClick={() => setVerPass(p => !p)}>
                                <FontAwesomeIcon icon={verPass ? faEyeSlash : faEye} />
                            </button>
                        </div>
                    </div>

                    <div className="rm-field">
                        <label htmlFor="rm-confirma">Confirmar contraseña</label>
                        <div className="rm-pass-wrap">
                            <input
                                id="rm-confirma"
                                type={verPass ? 'text' : 'password'}
                                placeholder="Repite tu nueva contraseña"
                                value={confirmaPass}
                                onChange={e => setConfirmaPass(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleNuevaContrasena()}
                                autoComplete="new-password"
                            />
                        </div>
                    </div>
                </>
            )}

            {error && <p className="rm-error">{error}</p>}
            {exito && <p className="rm-exito-msg">{exito}</p>}

            {!exito && (
                <button
                    type="button"
                    className="rm-submit-btn"
                    onClick={
                        paso === 1 ? handleEnviarPin
                        : paso === 2 ? handleValidarPin
                        : handleNuevaContrasena
                    }
                    disabled={loading}
                >
                    {loading          ? 'Procesando...'
                    : paso === 1      ? 'Enviar código'
                    : paso === 2      ? 'Verificar código'
                    :                   'Guardar contraseña'}
                </button>
            )}

            <p className="rm-footer">
                ¿Recordaste tu contraseña?{' '}
                <button type="button" onClick={onVolver}>Inicia sesión</button>
            </p>
        </div>
    );
}